import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import {
  getVisibleSidebarItems,
  applyFreemiumDashboardResultBoqAccess,
  canAccessDashboard,
  EMPTY_TENDER360_STATE_SCOPE,
  mergePlanFilters,
  normalizePlanDetails,
} from "../utils/planUtils";
import { fetchUserActivePlanDetails } from "../services/auth";
import { useAuth } from "./AuthContext";

const UserPlanContext = createContext(null);
const EMPTY_PLAN_FILTERS = Object.freeze({
  selectedcategories: Object.freeze([]),
  selectedstates: Object.freeze([]),
  zones: Object.freeze([]),
  tender360StateScope: EMPTY_TENDER360_STATE_SCOPE,
});

export function UserPlanProvider({ children }) {
  const { user, isAuthenticated, setUserPlans } = useAuth();
  const userId = user?.id ?? user?.uid ?? user?.user_id ?? null;

  const [plans, setPlans] = useState([]);
  const [planFilters, setPlanFilters] = useState(EMPTY_PLAN_FILTERS);
  const [visibleSidebarItems, setVisibleSidebarItems] = useState(new Set());
  const [canAccess, setCanAccess] = useState(true);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState(null);

  const applyPlanData = useCallback((rawPlans) => {
    const normalized = normalizePlanDetails({ plans: rawPlans });
    const list = normalized.plans || [];
    setPlans(list);
    const planTypes = list.map((p) => p.plan_type).filter(Boolean);
    const baseVisible = getVisibleSidebarItems(planTypes);
    setVisibleSidebarItems(applyFreemiumDashboardResultBoqAccess(planTypes, baseVisible));
    setCanAccess(canAccessDashboard(planTypes));
    setPlanFilters(mergePlanFilters(list));
  }, []);

  const setPlanDetails = useCallback((response) => {
    if (!response) {
      setPlans([]);
      setVisibleSidebarItems(new Set());
      setCanAccess(true);
      setPlanFilters(EMPTY_PLAN_FILTERS);
      return;
    }
    const rawPlans = response.plans || [];
    applyPlanData(rawPlans);
  }, [applyPlanData]);

  const loadPlanDetails = useCallback(async (uid) => {
    const id = uid ?? userId;
    if (id == null) return;
    setPlanLoading(true);
    setPlanError(null);
    try {
      const data = await fetchUserActivePlanDetails(id);
      setPlanDetails(data);
      setUserPlans(data);
    } catch (err) {
      setPlanError(err?.message || "Failed to load plan details.");
      setPlans([]);
      setVisibleSidebarItems(new Set());
      setCanAccess(true);
      setPlanFilters(EMPTY_PLAN_FILTERS);
    } finally {
      setPlanLoading(false);
    }
  }, [userId, setPlanDetails, setUserPlans]);

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      setPlans([]);
      setVisibleSidebarItems(new Set());
      setCanAccess(true);
      setPlanFilters(EMPTY_PLAN_FILTERS);
    }
  }, [isAuthenticated, userId]);

  const value = {
    plans,
    planFilters,
    visibleSidebarItems,
    canAccessDashboard: canAccess,
    setPlanDetails,
    loadPlanDetails,
    planLoading,
    planError,
  };

  return <UserPlanContext.Provider value={value}>{children}</UserPlanContext.Provider>;
}

export function useUserPlan() {
  const ctx = useContext(UserPlanContext);
  if (!ctx) {
    return {
      plans: [],
      planFilters: EMPTY_PLAN_FILTERS,
      visibleSidebarItems: new Set(),
      canAccessDashboard: true,
      setPlanDetails: () => {},
      loadPlanDetails: () => {},
      planLoading: false,
      planError: null,
    };
  }
  return ctx;
}

export default UserPlanContext;
